import {
  Box,
  Flex,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { PaginateNavigate } from "components/paginateComponent/PaginateComponent";

// Custom components
import * as React from "react";
import { useHistory } from "react-router-dom";
import { formaterDocument } from "utils/formaterDocumente";
import { useClients } from "hooks/ClientsHook";
import { useQuery } from "react-query";

type RowObj = {
  clientes: string;
  documento: number;
  cidade: string;
  terminais: string;
  status: any;
};

interface FilterClientsProps {
  nome?: string;
  documento?: string;
  cidade?: string;
}

const columnHelper = createColumnHelper<RowObj>();

// const columns = columnsDataCheck;
export default function ClientsTable(parms: FilterClientsProps) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const [paginate, setPaginate] = React.useState<number>(1);

  const { findClientsListByParms } = useClients();
  const { data, error, isLoading } = useQuery(
    ["findClientsListByParms", paginate, parms],
    () =>
      findClientsListByParms({
        page: paginate,
        ...parms,
      }),
    {
      retry: false,
    }
  );

  const navigate = useHistory();

  const dataTable: RowObj[] = Array.isArray(data?.data)
    ? data.data.map((item: any) => ({
        id: item?.ID,
        cidade: item?.CIDADE,
        clientes: item?.NOME,
        documento: item?.DOCUMENTO,
        status: "",
        terminais: item?.COUNT,
      }))
    : [];

  const columns = [
    columnHelper.accessor("clientes", {
      id: "clientes",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          NOME
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor("documento", {
      id: "documento",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          DOCUMENTO
        </Text>
      ),
      cell: (info) => (
        <Text color={textColorSecondary} fontSize="sm" fontWeight="500">
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor("cidade", {
      id: "cidade",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          CIDADE
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor("terminais", {
      id: "terminais",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          TERMINAIS
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="600">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
  ];

  const table = useReactTable({
    data: dataTable,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Flex
      direction="column"
      maxW={{ base: "100vw" }}
      overflowX={{ sm: "scroll", lg: "hidden" }}
    >
      {isLoading ? (
        <Box
          display="flex"
          alignItems="center"
          padding={30}
          justifyContent="center"
        >
          <Spinner width={100} height={100} />
        </Box>
      ) : error ? (
        <Flex w={"100%"} height={"100%"} justify={"center"} align={"center"}>
          <Text color={"red.500"} fontSize={"xl"}>
            {String(error)}
          </Text>
        </Flex>
      ) : (
        <>
          {}
          <Box>
            <Flex
              px="25px"
              mb="8px"
              justifyContent="space-between"
              align="center"
            >
              <Text
                color={textColor}
                fontSize="14px"
                fontWeight="700"
                lineHeight="100%"
              >
                TOTAL : {data?.total}
              </Text>
            </Flex>
            <Table variant="simple" color="gray.500" mt="12px">
              <Thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <Th
                          key={header.id}
                          colSpan={header.colSpan}
                          pe="10px"
                          borderColor={borderColor}
                          cursor="pointer"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          <Flex
                            justifyContent="space-between"
                            align="center"
                            fontSize={{ sm: "10px", lg: "12px" }}
                            color="gray.400"
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: "",
                              desc: "",
                            }[header.column.getIsSorted() as string] ?? null}
                          </Flex>
                        </Th>
                      );
                    })}
                  </Tr>
                ))}
              </Thead>
              <Tbody>
                {dataTable.map((data: any) => (
                  <Tr
                    key={data.id}
                    color={textColor}
                    cursor="pointer"
                    onClick={() =>
                      navigate.push(`/admin/clientInfo?idClient=${data.id}`)
                    }
                    _hover={{
                      opacity: 0.6,
                    }}
                  >
                    <Td
                      fontSize={{ sm: "13px" }}
                      minW={{ sm: "150px", md: "200px", lg: "auto" }}
                      borderColor="transparent"
                    >
                      {data.clientes.toUpperCase()}
                    </Td>
                    <Td
                      fontSize={{ sm: "13px" }}
                      minW={{ sm: "150px", md: "200px", lg: "auto" }}
                      borderColor="transparent"
                    >
                      {formaterDocument(data?.documento)}
                    </Td>
                    <Td
                      fontSize={{ sm: "13px" }}
                      minW={{ sm: "150px", md: "200px", lg: "auto" }}
                      borderColor="transparent"
                    >
                      {data.cidade}
                    </Td>
                    <Td
                      textAlign="center"
                      fontSize={{ sm: "13px" }}
                      minW={{ sm: "150px", md: "200px", lg: "auto" }}
                      borderColor="transparent"
                    >
                      {data.terminais}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          <PaginateNavigate
            current_page={data?.current_page}
            pages_preview={data?.links}
            pageSelect={setPaginate}
            lastPage={data?.last_page}
          />
        </>
      )}
    </Flex>
  );
}
